import notifications from 'notifications';
import * as TaskRunner from 'util/TaskRunner';

import type {ComponentKind} from '../newPredictionState';
import {FileImportError} from './FileImport';

export interface ExtractedComponent {
  name?: string;
  type: ComponentKind;
  value: string;
}

export interface ExtractedComplex {
  name: string;
  components: Array<ExtractedComponent>;
}

interface ExtractedComplexRaw {
  name: string;
  components: Array<{name?: string; type: string; value: string}>;
}

const COMPONENT_KINDS: Array<ComponentKind> = ['aminoAcids', 'smiles'];

export class FastaExtractionError extends FileImportError {
  constructor(message: string) {
    super('Failed to extract FASTA', message);
    this.name = 'FastaExtractionError';
  }
}

function isComponentKind(type: string): type is ComponentKind {
  return COMPONENT_KINDS.some((componentKind) => componentKind === type);
}

function parseExtractedComponent(
  complexName: string,
  component: ExtractedComplexRaw['components'][number]
): ExtractedComponent {
  if (!isComponentKind(component.type)) {
    throw new FastaExtractionError(
      `The FASTA extractor returned unsupported component type "${component.type}" for "${complexName}".`
    );
  }
  return {
    name: component.name,
    type: component.type,
    value: component.value,
  };
}

export async function extractComplexes(s3Locations: Array<string>): Promise<Array<ExtractedComplex>> {
  const result = await TaskRunner.postJSONPromise({
    url: '/1/api/model-hub/extract-components-from-files',
    data: {fileLocations: s3Locations},
  });
  if (result.errors && result.errors.length) {
    throw new FastaExtractionError(result.errors.join('\n'));
  }
  if (result.warnings && result.warnings.length) {
    notifications.warn(result.warnings.join('\n'));
  }
  return result.extractedComplexes.map((complex: ExtractedComplexRaw) => ({
    name: complex.name,
    components: complex.components.map((component) => parseExtractedComponent(complex.name, component)),
  }));
}
